/**
 * DepartmentsManager — Phase 3 (Jul-2026, User-Wunsch).
 *
 * Freidefinierbare Abteilungen (z. B. Verkauf, Werkstatt, Buchhaltung) mit
 * einer Modul-Checkliste (freigeschaltete App-Bereiche). Mitarbeiter werden
 * einer Abteilung zugeordnet und erben deren Module. Die Abteilung ersetzt
 * langfristig die freie „Funktion".
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, Check, X as XIcon, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function DepartmentsManager({ onChanged }) {
    const [open, setOpen] = useState(false);
    const [modules, setModules] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // dept | "new" | null

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [dep, cat] = await Promise.all([
                api.get("/dealer/departments"),
                api.get("/dealer/department-modules"),
            ]);
            setItems(dep.data?.items || []);
            setModules(cat.data?.modules || []);
        } catch { /* noop */ } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const moduleLabel = (key) => modules.find((m) => m.key === key)?.label || key;

    const remove = async (d) => {
        if (!window.confirm(`Abteilung „${d.name}“ löschen? Zugeordnete Mitarbeiter behalten ihre Funktion, verlieren aber die Abteilungs-Verknüpfung.`)) return;
        try {
            await api.delete(`/dealer/departments/${d.id}`);
            toast.success("Abteilung gelöscht");
            load(); onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm mb-6" data-testid="departments-manager">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid="departments-toggle"
            >
                <span className="flex items-center gap-2 font-display font-black text-lg">
                    <Building2 className="w-5 h-5 text-[#FFCC00]" /> Abteilungen
                    <span className="text-xs font-normal text-gray-400">({items.length})</span>
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mt-3 mb-3">
                        Abteilungen bündeln freigeschaltete App-Bereiche (Module). Ein Mitarbeiter erbt die Module seiner Abteilung — individuelle Anpassungen bleiben pro Mitarbeiter möglich.
                    </p>

                    {loading ? (
                        <div className="text-sm text-gray-500">Lade…</div>
                    ) : (
                        <div className="space-y-2">
                            {items.map((d) => (
                                <div key={d.id} className="flex items-start justify-between gap-3 border border-gray-200 rounded-sm px-3 py-2" data-testid={`department-row-${d.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-bold text-sm flex items-center gap-2">
                                            {d.name}
                                            <span className="text-[10px] font-normal text-gray-400">{d.employee_count || 0} Mitarbeiter</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {(d.modules || []).length === 0 ? (
                                                <span className="text-[10px] text-gray-400">Keine Module</span>
                                            ) : (d.modules || []).map((m) => (
                                                <span key={m} className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700 border border-gray-200 rounded-sm px-1.5 py-0.5">{moduleLabel(m)}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => setEditing(d)} className="p-1.5 hover:bg-gray-100 rounded-sm text-[#111827]" title="Bearbeiten" data-testid={`department-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(d)} className="p-1.5 hover:bg-red-50 rounded-sm text-red-700" title="Löschen" data-testid={`department-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                            {items.length === 0 && (
                                <div className="text-sm text-gray-500 py-2">Noch keine Abteilungen angelegt.</div>
                            )}
                        </div>
                    )}

                    <Button
                        type="button"
                        onClick={() => setEditing("new")}
                        className="mt-3 bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider text-xs"
                        data-testid="department-new-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Neue Abteilung
                    </Button>
                </div>
            )}

            {editing && (
                <DepartmentEditModal
                    dept={editing === "new" ? null : editing}
                    modules={modules}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); onChanged?.(); }}
                />
            )}
        </div>
    );
}

function DepartmentEditModal({ dept, modules, onClose, onSaved }) {
    const [name, setName] = useState(dept?.name || "");
    const [selected, setSelected] = useState(new Set(dept?.modules || []));
    const [busy, setBusy] = useState(false);

    const toggle = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("Name ist Pflicht"); return; }
        setBusy(true);
        try {
            const payload = { name: name.trim(), modules: Array.from(selected) };
            if (dept) await api.patch(`/dealer/departments/${dept.id}`, payload);
            else await api.post("/dealer/departments", payload);
            toast.success(dept ? "Abteilung gespeichert" : "Abteilung angelegt");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="department-edit-modal">
            <div className="bg-white max-w-md w-full rounded-sm shadow-xl max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="font-display font-black text-lg">{dept ? "Abteilung bearbeiten" : "Neue Abteilung"}</div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="department-modal-close"><XIcon className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Name der Abteilung *</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} placeholder="z.B. Verkauf, Werkstatt, Buchhaltung" className="rounded-sm mt-1" data-testid="department-name-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Freigeschaltete Bereiche (Module)</label>
                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                            {modules.map((m) => (
                                <label key={m.key} className={`flex items-center gap-2 rounded-sm border px-2.5 py-1.5 cursor-pointer text-sm ${selected.has(m.key) ? "border-[#111827] bg-amber-50" : "border-gray-200 hover:border-gray-400"}`} data-testid={`department-module-${m.key}`}>
                                    <input type="checkbox" checked={selected.has(m.key)} onChange={() => toggle(m.key)} className="w-4 h-4" />
                                    <span className="font-semibold">{m.label}</span>
                                    {selected.has(m.key) && <Check className="w-3.5 h-3.5 text-emerald-600 ml-auto" />}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" onClick={onClose} variant="outline" className="rounded-sm">Abbrechen</Button>
                        <Button type="submit" disabled={busy} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="department-save-btn">
                            {busy ? "Speichere…" : "Speichern"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
